import { apiUtils } from '@api';
import {
    createDate, getRandomEnumValue, getRandomItemFromArray, randomBoolean, randomNum, delay, toISO
} from '@tsp-ui/utils';
import { getCalendarDays, getMonth } from '@views/closing-calendar/ClosingCalendarPage';
import { parseISO } from 'date-fns';


export enum LoanPurpose {
    PURCHASE = 'PURCHASE',
    REFINANCE = 'REFINANCE',
    REFINANCE_RATE_TERM = 'REFINANCE_RATE_TERM',
    REFINANCE_CASH_OUT = 'REFINANCE_CASH_OUT'
}

export const loanPurposeDisplay = {
    [LoanPurpose.PURCHASE]: 'Purchase',
    [LoanPurpose.REFINANCE]: 'Refinance',
    [LoanPurpose.REFINANCE_RATE_TERM]: 'Refinance rate term',
    [LoanPurpose.REFINANCE_CASH_OUT]: 'Refinance cash out'
};

const MOCK_BACKEND = false;

let mockDays: {[key: string]: CalendarDay} = {};
export function setMockDays(fn: (prevState: typeof mockDays) => typeof mockDays) {
    mockDays = fn(mockDays);
}

export async function getDays(startDay: Date, endDay: Date): Promise<CalendarDay[]> {
    const days = await (MOCK_BACKEND ? new Promise<CalendarDay[]>(async (res) => {
        await delay(1000);

        const date = createDate(10, startDay);
        const month = getMonth(date);
        const year = String(date.getFullYear());

        res(getCalendarDays(month, year).map(date => {
            let day = mockDays[toISO(date)];

            if (!day) {
                day = {
                    date: date.toISOString(),
                    loansClosing: randomNum(4, 22),
                    availableCapacity: randomNum(18, 22),
                    availableClosers: randomNum(2, 5),
                    closingVolume: randomNum(20, 100) * 10000
                };
                mockDays[toISO(date)] = day;
            }

            return day;
        }));
    }) : apiUtils.get('/products/closing-calendar/day', {
        startDay: startDay.toISOString(),
        endDay: endDay.toISOString()
    })) as CalendarDay[];

    return days.map(day => ({
        ...day,
        date: toISO(parseISO(day.date))
    }));
}

let associateID = 0;
const associates: Associate[] = [
    {
        id: String(associateID++),
        name: 'James Madison'
    },
    {
        id: String(associateID++),
        name: 'Derrick Louis'
    },
    {
        id: String(associateID++),
        name: 'Amy Steinfeldt'
    },
    {
        id: String(associateID++),
        name: 'Georgia Galway'
    }
];

let loanID = 0;
const loanNumber = 70023481;
function getLoans(): Loan[] {
    return [ ...Array(randomNum(10, 25)) ].map(() => ({
        id: String(loanID++),
        loanNumber: String(loanNumber),
        closerAssociateId: getRandomItemFromArray(associates).id,
        processorAssociateName: getRandomItemFromArray(associates).name,
        underwriterAssociateName: getRandomItemFromArray(associates).name,
        borrowerNames: [ 'John Adams', 'Jay Barker' ],
        loanPurpose: getRandomEnumValue(LoanPurpose),
        currentMilestone: 'Completion',
        lockExpiration: createDate(360).toISOString(),
        loanProgramName: 'FNMA 30 Fixed',
        interestRate: randomNum(5, 10, 3) / 100,
        loanAmount: randomNum(25, 200) * 1000,
        lockPeriod: getRandomItemFromArray([
            15, 30, 45
        ]),
        propertyAddress: {
            street: '123 Union St',
            city: 'Houston',
            state: 'TX',
            zip: '66043'
        },
        titleCompany: 'Nations Title Agency, Inc',
        closingDate: createDate(5).toISOString(),
        lockDate: createDate(-30).toISOString(),
        applicationDate: createDate(-25).toISOString(),
        approvedDate: createDate(-10).toISOString(),
        clearToCloseDate: randomBoolean() ? createDate(-5).toISOString() : undefined,
        estimatedClosingDate: createDate(5).toISOString()
    }));
}

export function getDayDetails(day: string): Promise<CalendarDayDetail> {
    return MOCK_BACKEND ? new Promise(async (res) => {
        await delay(1000);
        const loans = getLoans();

        res({
            date: day,
            closerCapacities: associates.map(({ id }) => {
                const numAssigned = loans.filter(loan => loan.closerAssociateId === id).length;
                const capacity = randomNum(4, 9);

                return {
                    closerAssociateId: id,
                    capacity: capacity >= numAssigned ? capacity : numAssigned
                };
            }),
            loans
        });
    }) : apiUtils.get(`/products/closing-calendar/day/${day}`);
}

export function getAssociates(): Promise<Associate[]> {
    return MOCK_BACKEND ? new Promise(async (res) => {
        await delay(1000);

        res(associates);
    }) : apiUtils.get('/products/closing-calendar/associates');
}

export function updateClosing(loanID: string, closerAssociateId: string, closingDate: Date): Promise<Loan> {
    return MOCK_BACKEND ? new Promise(async (res) => {
        await delay(1000);

        res({
            id: loanID,
            closerAssociateId,
            closingDate: toISO(closingDate)
        } as Loan);
    }) : apiUtils.patch(`/products/closing-calendar/loan/${loanID}`, {
        closerAssociateId,
        closingDate: toISO(closingDate)
    });
}

export interface CalendarDay {
    date: string;
    availableClosers: number;
    availableCapacity: number;
    loansClosing: number;
    closingVolume: number;
}

export interface CloserCapacity {
    closerAssociateId: string;
    capacity: number;
}

export interface CalendarDayDetail {
    date: string;
    closerCapacities: CloserCapacity[];
    loans: Loan[];
}

export interface Associate {
    id: string;
    name: string;
}

export interface Loan {
    id: string;
    loanNumber: string;
    closerAssociateId: string;
    processorAssociateName: string;
    underwriterAssociateName: string;
    borrowerNames: string[];
    loanPurpose: LoanPurpose;
    currentMilestone: string;
    lockExpiration?: string;
    loanProgramName: string;
    interestRate?: number;
    loanAmount?: number;
    lockPeriod?: number;
    propertyAddress: Address;
    titleCompany: string;
    closingDate?: string;
    lockDate?: string;
    applicationDate?: string;
    approvedDate?: string;
    clearToCloseDate?: string;
    estimatedClosingDate?: string;
}

interface Address {
    street: string;
    line2?: string;
    city: string;
    state: string;
    zip: string;
    county?: string;
}
